/**
 * `/admin/mcp` URL search 계약 — 사고 대응 결과를 링크로 공유하고 새로고침해도 유지되게
 * 탭·보기·필터·페이지를 전부 URL 에 싣는다.
 *
 * 키 이름은 서버 파라미터(`AdminListDelegatedCallsParams`)를 그대로 따른다 — 링크만 보고도
 * 어떤 조회였는지 알 수 있게. 연동 탭 전용 키만 이름을 따로 둔다(status·admins·gpage).
 */
import type {
  AdminListDelegatedCallsParams,
  AdminListDelegatedCallsSubject,
  AdminListDelegatedGrantsStatus,
} from '@/api/schemas'

import { DATE_RE, KST_INPUT_RE, kstInputToIso } from './kst'

export type McpTab = 'grants' | 'calls'
export type CallsView = 'list' | 'grant'

export interface McpSearch {
  tab?: McpTab

  // --- 연동 탭 ---
  status?: AdminListDelegatedGrantsStatus
  /** true 면 관리자 연동도 포함(subject=all), 아니면 수강생만(campus) */
  admins?: boolean
  gpage?: number

  // --- 호출 기록 탭 ---
  view?: CallsView
  /** KST datetime-local 값('YYYY-MM-DDTHH:mm'). 없으면 최근 7일 */
  since?: string
  until?: string
  subject?: AdminListDelegatedCallsSubject
  campus_user_id?: string
  grant_id?: string
  shop_id?: string
  channel?: string
  /**
   * 도구(operation) — 화면은 **하나만** 고른다. 서버는 반복 파라미터(OR)를 받지만 생성된 URL 빌더가
   * 배열을 `toString()`(쉼표 결합)으로 보내 'a,b' 한 값이 돼 버린다. 하나면 그 문제가 없다.
   */
  operation?: string
  outcome?: string
  failures_only?: boolean
  /** 받은 데이터 날짜 겹침 구간(YYYY-MM-DD). 둘 다 있거나 둘 다 없다 */
  data_from?: string
  data_to?: string
  /** [사고 대응] 으로 들어온 상태 — 상단 안내 문장을 띄운다 */
  incident?: boolean
  page?: number
}

/** 호출 기록 필터 키 — [초기화]·필터 바 재마운트 키 계산에 쓴다 */
export const CALL_FILTER_KEYS = [
  'since',
  'until',
  'subject',
  'campus_user_id',
  'grant_id',
  'shop_id',
  'channel',
  'operation',
  'outcome',
  'failures_only',
  'data_from',
  'data_to',
  'incident',
] as const satisfies readonly (keyof McpSearch)[]

// --- validateSearch 용 파서들 — 이상한 값은 undefined 로 떨어뜨려 기본값을 쓰게 한다 ---

/** 문자열 id. 기본 search 파서가 숫자처럼 생긴 값을 number 로 바꿀 수 있어 되돌린다 */
function str(v: unknown): string | undefined {
  if (typeof v === 'string' && v.trim() !== '') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return undefined
}

function pick<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined
}

function bool(v: unknown): boolean | undefined {
  return v === true || v === 'true' ? true : undefined
}

function pageNum(v: unknown): number | undefined {
  const n = typeof v === 'string' ? Number(v) : v
  return typeof n === 'number' && Number.isInteger(n) && n > 1 ? n : undefined
}

function matching(v: unknown, re: RegExp): string | undefined {
  return typeof v === 'string' && re.test(v) ? v : undefined
}

export function validateMcpSearch(raw: Record<string, unknown>): McpSearch {
  // 배열로 들어온 옛 링크·손으로 고친 링크는 첫 값만 쓴다
  const rawOp = Array.isArray(raw.operation) ? raw.operation[0] : raw.operation
  return {
    tab: pick(raw.tab, ['grants', 'calls'] as const),
    status: pick(raw.status, ['active', 'revoked', 'all'] as const),
    admins: bool(raw.admins),
    gpage: pageNum(raw.gpage),
    view: pick(raw.view, ['list', 'grant'] as const),
    since: matching(raw.since, KST_INPUT_RE),
    until: matching(raw.until, KST_INPUT_RE),
    subject: pick(raw.subject, ['campus', 'admin', 'all'] as const),
    campus_user_id: str(raw.campus_user_id),
    grant_id: str(raw.grant_id),
    shop_id: str(raw.shop_id),
    channel: str(raw.channel),
    operation: str(rawOp),
    outcome: str(raw.outcome),
    failures_only: bool(raw.failures_only),
    data_from: matching(raw.data_from, DATE_RE),
    data_to: matching(raw.data_to, DATE_RE),
    incident: bool(raw.incident),
    page: pageNum(raw.page),
  }
}

/**
 * URL search → 호출 기록 API 파라미터.
 * since·until 은 URL 에 없을 때 쓸 기본 구간을 호출자가 넘긴다(렌더마다 바뀌지 않게 한 번만 계산).
 */
export function toCallParams(
  s: McpSearch,
  range: { since: string; until: string },
): AdminListDelegatedCallsParams {
  // outcome 과 failures_only 는 서버가 동시 사용을 422 로 막는다 — 실패만이 켜져 있으면 outcome 을 버린다
  const failuresOnly = s.failures_only === true
  // 겹침 날짜는 짝이 맞을 때만 보낸다(한쪽만이면 서버 422)
  const hasDataRange = !!s.data_from && !!s.data_to
  return {
    since: kstInputToIso(range.since),
    until: kstInputToIso(range.until),
    subject: s.subject ?? 'campus',
    campus_user_id: s.campus_user_id,
    grant_id: s.grant_id,
    shop_id: s.shop_id,
    channel: s.channel,
    operation: s.operation ? [s.operation] : undefined,
    outcome: failuresOnly ? undefined : s.outcome,
    failures_only: failuresOnly || undefined,
    data_from: hasDataRange ? s.data_from : undefined,
    data_to: hasDataRange ? s.data_to : undefined,
    page: s.page ?? 1,
    per_page: 50,
  }
}

/**
 * 4xx(특히 422 검증 실패)는 다시 보내도 같은 답이라 재시도하지 않는다.
 * custom-instance 는 `{ status, ...body }` 평문 객체를 던진다.
 */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const status =
    error && typeof error === 'object' && 'status' in error ? (error as { status?: unknown }).status : undefined
  if (typeof status === 'number' && status >= 400 && status < 500) return false
  return failureCount < 1
}
